import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, Pagination } from "@/hq/HQPrimitives";

const TABS = [
  { key: "orders",        label: "Orders" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "payments",      label: "Payments" },
];

const CURRENCY_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };

function money(minor, currency) {
  if (minor == null || minor === undefined) return "—";
  const value = Number(minor) / 100;
  const isWhole = Math.abs(value - Math.round(value)) < 0.005;
  const s = isWhole ? String(Math.round(value)) : value.toFixed(2);
  return `${CURRENCY_SYM[currency] || (currency + " ")}${s}`;
}
function amount(a, currency) {
  if (a == null) return "—";
  return `${CURRENCY_SYM[currency] || (currency + " ")}${Number(a).toFixed(2)}`;
}
function StatusPill({ value, tone = {} }) {
  const t = tone[value] || { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
          style={{ background: t.bg, color: t.fg, border: `1px solid ${t.bd}` }}>
      {value || "—"}
    </span>
  );
}
const ORDER_TONE = {
  pending:   { bg: "#FFF9E6", fg: "#8A6D00", bd: "#F5E4B0" },
  active:    { bg: "#EBFBF2", fg: "#0FA958", bd: "#B7EBCF" },
  cancelled: { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
  failed:    { bg: "#FDECE7", fg: "#C1350B", bd: "#F6C0AF" },
};
const PAY_TONE = {
  succeeded: { bg: "#EBFBF2", fg: "#0FA958", bd: "#B7EBCF" },
  failed:    { bg: "#FDECE7", fg: "#C1350B", bd: "#F6C0AF" },
  pending:   { bg: "#FFF9E6", fg: "#8A6D00", bd: "#F5E4B0" },
  refunded:  { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
};

function SummaryStrip({ summary }) {
  if (!summary) return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[0,1,2,3].map(i => <Card key={i} className="p-5"><Skeleton h={28} w={80}/></Card>)}
    </div>
  );
  const mrr = summary.mrr_by_currency || {};
  const pays = summary.payments_by_currency || {};
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-5" data-testid="hq-commercial-active-subs">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Active subscriptions</div>
          <div className="mt-3 font-black text-3xl tracking-[-0.02em]">{summary.active_subs_total}</div>
        </Card>
        <Card className="p-5" data-testid="hq-commercial-payments-received">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Payments received</div>
          <div className="mt-3 font-black text-3xl tracking-[-0.02em]">{summary.payments_received_total}</div>
        </Card>
        <Card className="p-5" data-testid="hq-commercial-mrr">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">MRR by currency</div>
          <div className="mt-3 space-y-1">
            {Object.keys(mrr).length === 0 && <div className="text-[13px] text-[#8A8A8A]">No active subs yet</div>}
            {Object.entries(mrr).map(([cur, row]) => (
              <div key={cur} className="flex items-baseline justify-between text-[14px]">
                <div className="font-black">{cur}</div>
                <div className="font-black">{money(row.mrr_minor, cur)}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5" data-testid="hq-commercial-cash">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Payments by currency</div>
          <div className="mt-3 space-y-1">
            {Object.keys(pays).length === 0 && <div className="text-[13px] text-[#8A8A8A]">No payments yet</div>}
            {Object.entries(pays).map(([cur, minor]) => (
              <div key={cur} className="flex items-baseline justify-between text-[14px]">
                <div className="font-black">{cur}</div>
                <div className="font-black">{money(minor, cur)}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

function OrdersTab() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25 };
    if (status) params.status = status;
    hqApi.get("/optimise/orders", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ items: [], total: 0, error: true }));
    return () => { alive = false; };
  }, [page, status]);

  const items = data?.items || [];
  return (
    <div className="space-y-4" data-testid="hq-orders-tab">
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-[#F6F5F2] p-1">
            {[["", "All"], ["pending", "Pending"], ["active", "Active"], ["cancelled", "Cancelled"], ["failed", "Failed"]].map(([v, l]) => (
              <button key={v || "all"} type="button" onClick={() => { setStatus(v); setPage(1); }}
                      data-testid={`hq-orders-status-${v || "all"}`}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase transition-colors ${status === v ? "bg-black text-white" : "text-black/60 hover:text-black"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]">
            {data ? `${data.total} order${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        {!data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No orders yet" hint="Customer checkouts will show here." testId="hq-orders-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Business</th>
                  <th className="text-left px-5 py-3 font-bold">Website</th>
                  <th className="text-left px-5 py-3 font-bold">Plan</th>
                  <th className="text-left px-5 py-3 font-bold">Cycle</th>
                  <th className="text-left px-5 py-3 font-bold">Amount</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">When</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-order-row-${o.id}`}>
                    <td className="px-5 py-3 font-bold">{o.business?.name || o.customer_name || "—"}<div className="text-[11px] text-[#8A8A8A] font-normal">{o.customer_email || ""}</div></td>
                    <td className="px-5 py-3 text-[13px] break-all">{o.website?.normalized_domain || o.website_url || "—"}</td>
                    <td className="px-5 py-3 text-[13px]">{o.plan?.name?.replace("Orange Optimise · ", "") || o.plan_key}</td>
                    <td className="px-5 py-3 text-[11px] tracking-[0.16em] uppercase text-[#4A4A4A]">{o.cycle}</td>
                    <td className="px-5 py-3 font-black">{money(o.amount_minor, o.currency)}</td>
                    <td className="px-5 py-3"><StatusPill value={o.status} tone={ORDER_TONE} /></td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-orders-page" />}
    </div>
  );
}

function SubscriptionsTab({ onCancel }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = () => {
    setData(null);
    const params = { page, page_size: 25 };
    if (status) params.status = status;
    return hqApi.get("/optimise/subscriptions", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0, error: true }));
  };

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25 };
    if (status) params.status = status;
    hqApi.get("/optimise/subscriptions", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ items: [], total: 0, error: true }));
    return () => { alive = false; };
  }, [page, status]);

  const cancel = async (sub) => {
    if (!window.confirm(`Cancel this subscription for ${sub?.business?.name || sub?.plan_key}? Monitoring will stop.`)) return;
    setCancelling(sub.id);
    try {
      await hqApi.post(`/optimise/subscriptions/${sub.id}/cancel`);
      await load();
      onCancel?.();
    } catch { /* toast optional */ }
    finally { setCancelling(null); }
  };

  const items = data?.items || [];
  return (
    <div className="space-y-4" data-testid="hq-subscriptions-tab">
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-[#F6F5F2] p-1">
            {[["", "All"], ["pending", "Pending"], ["active", "Active"], ["cancelled", "Cancelled"], ["past_due", "Past due"]].map(([v, l]) => (
              <button key={v || "all"} type="button" onClick={() => { setStatus(v); setPage(1); }}
                      data-testid={`hq-subs-status-${v || "all"}`}
                      className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase transition-colors ${status === v ? "bg-black text-white" : "text-black/60 hover:text-black"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]">
            {data ? `${data.total} subscription${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        {!data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No subscriptions yet" hint="Once a checkout activates via PayPal, the subscription will appear here." testId="hq-subs-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Business</th>
                  <th className="text-left px-5 py-3 font-bold">Plan</th>
                  <th className="text-left px-5 py-3 font-bold">Cycle</th>
                  <th className="text-left px-5 py-3 font-bold">Amount</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Provider ID</th>
                  <th className="text-left px-5 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-sub-row-${s.id}`}>
                    <td className="px-5 py-3 font-bold">{s.business?.name || "—"}</td>
                    <td className="px-5 py-3 text-[13px]">{s.plan?.name?.replace("Orange Optimise · ", "") || s.plan_key}</td>
                    <td className="px-5 py-3 text-[11px] tracking-[0.16em] uppercase text-[#4A4A4A]">{s.billing_interval}</td>
                    <td className="px-5 py-3 font-black">{money(s.amount_minor, s.currency)}</td>
                    <td className="px-5 py-3"><StatusPill value={s.status} tone={ORDER_TONE} /></td>
                    <td className="px-5 py-3 text-[11px] text-[#8A8A8A] break-all">{s.provider_subscription_id || "—"}</td>
                    <td className="px-5 py-3">
                      {s.status !== "cancelled" ? (
                        <button type="button" onClick={() => cancel(s)} disabled={cancelling === s.id}
                                data-testid={`hq-sub-cancel-${s.id}`}
                                className="px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-white text-[10px] font-black tracking-[0.2em] uppercase hover:border-[#C1350B] hover:text-[#C1350B] disabled:opacity-50">
                          {cancelling === s.id ? "Cancelling…" : "Cancel"}
                        </button>
                      ) : (<span className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A]">Cancelled</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-subs-page" />}
    </div>
  );
}

function PaymentsTab() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    hqApi.get("/optimise/payments", { params: { page, page_size: 25 } })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ items: [], total: 0 }));
    return () => { alive = false; };
  }, [page]);

  const items = data?.items || [];
  return (
    <div className="space-y-4" data-testid="hq-payments-tab">
      <Card className="p-4 flex items-center">
        <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]">
          {data ? `${data.total} payment${data.total === 1 ? "" : "s"}` : "…"}
        </div>
      </Card>
      <Card className="p-0 overflow-hidden">
        {!data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No payments yet" hint="Recurring payments from PayPal will appear here." testId="hq-payments-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Business</th>
                  <th className="text-left px-5 py-3 font-bold">Amount</th>
                  <th className="text-left px-5 py-3 font-bold">Type</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Provider ID</th>
                  <th className="text-left px-5 py-3 font-bold">Paid</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-payment-row-${p.id}`}>
                    <td className="px-5 py-3 font-bold">{p.business?.name || "—"}</td>
                    <td className="px-5 py-3 font-black">{amount(p.amount, p.currency)}</td>
                    <td className="px-5 py-3 text-[11px] tracking-[0.16em] uppercase text-[#4A4A4A]">{p.payment_type}</td>
                    <td className="px-5 py-3"><StatusPill value={p.status} tone={PAY_TONE} /></td>
                    <td className="px-5 py-3 text-[11px] text-[#8A8A8A] break-all">{p.provider_payment_id || "—"}</td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">{p.paid_at ? new Date(p.paid_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-payments-page" />}
    </div>
  );
}

export default function HQOptimiseCommercial() {
  const [tab, setTab] = useState("orders");
  const [summary, setSummary] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    hqApi.get("/optimise/commercial/summary")
      .then((r) => alive && setSummary(r.data))
      .catch(() => alive && setSummary({}));
    return () => { alive = false; };
  }, [nonce]);

  return (
    <div className="space-y-4" data-testid="hq-optimise-commercial">
      <SummaryStrip summary={summary} />
      <div className="inline-flex rounded-full bg-[#F6F5F2] p-1" data-testid="hq-commercial-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  data-testid={`hq-commercial-tab-${t.key}`}
                  className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.2em] uppercase transition-colors ${tab === t.key ? "bg-black text-white" : "text-black/60 hover:text-black"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "orders"        && <OrdersTab />}
      {tab === "subscriptions" && <SubscriptionsTab onCancel={() => setNonce((n) => n + 1)} />}
      {tab === "payments"      && <PaymentsTab />}
    </div>
  );
}
